import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Eye, Plus, UserCheck, UserX, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const emptyWalkIn = {
  customer_name: '', customer_email: '', customer_phone: '',
  booking_type: 'class', class_id: '', studio_id: '',
  booking_date: '', booking_time: '', hours: 1, pax: 1, rental_purpose: '',
  payment_status: 'confirmed', payment_method: 'cash', attended: true, notes: '',
};

const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [proofImage, setProofImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [classes, setClasses] = useState([]);
  const [studios, setStudios] = useState([]);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [walkIn, setWalkIn] = useState(emptyWalkIn);
  const [savingWalkIn, setSavingWalkIn] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [bookedFrom, setBookedFrom] = useState('');
  const [bookedTo, setBookedTo] = useState('');
  const [isOwner, setIsOwner] = useState(true);
  const [meta, setMeta] = useState({ total: 0, counts: { all: 0, pending: 0, confirmed: 0 }, confirmed_count: 0, payout: 0, payout_by_method: {} });
  const [loadingMore, setLoadingMore] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetchOptions();
    fetchRole();
  }, []);

  useEffect(() => {
    fetchBookings('reset');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, typeFilter, dateFrom, dateTo, bookedFrom, bookedTo]);

  useEffect(() => {
    setProofImage(null);
    if (selectedBooking?.has_payment_proof) {
      fetchProof(selectedBooking.id);
    }
  }, [selectedBooking]);

  const fetchOptions = async () => {
    try {
      const [cls, std] = await Promise.all([
        axios.get(`${API}/classes`),
        axios.get(`${API}/studios`),
      ]);
      setClasses(cls.data);
      setStudios(std.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchRole = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
      setIsOwner((res.data.role || 'owner') === 'owner');
    } catch (error) {
      console.error('Error fetching role:', error);
    }
  };

  const fetchProof = async (bookingId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/bookings/${bookingId}/proof`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProofImage(response.data.payment_proof_url);
    } catch (error) {
      console.error('Error fetching proof:', error);
    }
  };

  // mode: 'reset' = first page, 'more' = next page, 'refresh' = reload what's already shown
  const fetchBookings = async (mode = 'refresh') => {
    const reqId = ++requestIdRef.current;
    const skip = mode === 'more' ? bookings.length : 0;
    const limit = mode === 'refresh' ? Math.max(PAGE_SIZE, bookings.length) : PAGE_SIZE;
    const toIso = (v) => (v ? new Date(v).toISOString() : undefined);
    const params = {
      skip, limit, status: filter, booking_type: typeFilter,
      date_from: dateFrom || undefined, date_to: dateTo || undefined,
      booked_from: toIso(bookedFrom), booked_to: toIso(bookedTo),
    };
    if (mode === 'more') setLoadingMore(true);
    try {
      const token = localStorage.getItem('admin_token');
      const { data } = await axios.get(`${API}/bookings`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (reqId !== requestIdRef.current) return;
      setBookings((prev) => (mode === 'more' ? [...prev, ...data.items] : data.items));
      setMeta(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      if (mode === 'more') setLoadingMore(false);
    }
  };

  const handleWalkInSubmit = async (e) => {
    e.preventDefault();
    if (!walkIn.customer_name || !walkIn.booking_date) {
      toast.error('Name and date are required');
      return;
    }
    if (walkIn.booking_type === 'class' && !walkIn.class_id) {
      toast.error('Please select a class');
      return;
    }
    if (walkIn.booking_type === 'studio' && !walkIn.studio_id) {
      toast.error('Please select a studio');
      return;
    }
    setSavingWalkIn(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        customer_name: walkIn.customer_name,
        customer_email: walkIn.customer_email || null,
        customer_phone: walkIn.customer_phone,
        booking_type: walkIn.booking_type,
        class_id: walkIn.booking_type === 'class' ? walkIn.class_id : null,
        studio_id: walkIn.booking_type === 'studio' ? walkIn.studio_id : null,
        booking_date: walkIn.booking_date,
        booking_time: walkIn.booking_time || null,
        hours: walkIn.booking_type === 'studio' ? Number(walkIn.hours) : null,
        pax: walkIn.booking_type === 'studio' ? Number(walkIn.pax) : null,
        rental_purpose: walkIn.booking_type === 'studio' ? (walkIn.rental_purpose.trim() || null) : null,
        payment_status: walkIn.payment_status,
        payment_method: walkIn.payment_method,
        attended: walkIn.attended,
        notes: walkIn.notes.trim() || null,
      };
      await axios.post(`${API}/admin/walk-in-bookings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Walk-in booking created');
      setWalkInOpen(false);
      setWalkIn(emptyWalkIn);
      fetchBookings();
    } catch (error) {
      console.error('Error creating walk-in:', error);
      toast.error(error.response?.data?.detail || 'Failed to create walk-in booking');
    } finally {
      setSavingWalkIn(false);
    }
  };

  const handleToggleAttendance = async (booking) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/bookings/${booking.id}/attendance`, { attended: !booking.attended }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBookings();
      if (selectedBooking?.id === booking.id) {
        setSelectedBooking({ ...selectedBooking, attended: !booking.attended });
      }
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error('Failed to update attendance');
    }
  };

  const handleVerifyPayment = async (bookingId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/bookings/${bookingId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment verified and confirmation email sent!');
      fetchBookings();
      setSelectedBooking(null);
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Failed to verify payment');
    }
  };

  const handleRejectPayment = async (bookingId) => {
    if (!window.confirm('Reject this screenshot and email the customer to upload a new one?')) {
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/bookings/${bookingId}/reject`, { base_url: window.location.origin }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Screenshot rejected — re-upload request emailed to customer');
      fetchBookings();
      setSelectedBooking(null);
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast.error('Failed to reject payment');
    }
  };

  const filteredBookings = bookings;
  const hasMore = bookings.length < meta.total;

  const peso = (n) => `₱${Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const payoutByMethod = meta.payout_by_method || {};
  const methodLabels = { cash: 'Cash', gcash: 'GCash', online: 'Online' };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filteredBookings.length > 0 && filteredBookings.every((b) => selected.has(b.id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filteredBookings.forEach((b) => next.delete(b.id));
      } else {
        filteredBookings.forEach((b) => next.add(b.id));
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected booking(s)? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/bookings/delete`, { ids }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${ids.length} booking(s) deleted`);
      setSelected(new Set());
      fetchBookings();
    } catch (error) {
      console.error('Error deleting bookings:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete bookings');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this booking? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Booking deleted');
      setSelected((prev) => { const n = new Set(prev); n.delete(bookingId); return n; });
      if (selectedBooking?.id === bookingId) setSelectedBooking(null);
      fetchBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  const handleRevertToPending = async (bookingId) => {
    if (!window.confirm('Revert this confirmed booking back to pending? It will free up the class slot.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/bookings/${bookingId}/unconfirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Booking reverted to pending');
      fetchBookings();
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking({ ...selectedBooking, payment_status: 'pending' });
      }
    } catch (error) {
      console.error('Error reverting booking:', error);
      toast.error('Failed to revert booking');
    }
  };

  return (
    <div className="p-8" data-testid="bookings-management">
      <div className="mb-8">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
            Bookings Management
          </h2>
          <button
            onClick={() => setWalkInOpen(true)}
            className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center gap-2"
            data-testid="add-walk-in-btn"
          >
            <Plus size={20} /> ADD WALK-IN BOOKING
          </button>
        </div>
        
        {/* Filter Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              filter === 'all' ? 'bg-black text-white' : 'bg-zinc-200 text-black hover:bg-zinc-300'
            }`}
            data-testid="filter-all"
          >
            All ({meta.counts.all})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              filter === 'pending' ? 'bg-[var(--accent)] text-black' : 'bg-zinc-200 text-black hover:bg-zinc-300'
            }`}
            data-testid="filter-pending"
          >
            Pending ({meta.counts.pending})
          </button>
          <button
            onClick={() => setFilter('confirmed')}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              filter === 'confirmed' ? 'bg-green-500 text-white' : 'bg-zinc-200 text-black hover:bg-zinc-300'
            }`}
            data-testid="filter-confirmed"
          >
            Confirmed ({meta.counts.confirmed})
          </button>
          {selected.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="ml-auto px-4 py-2 rounded-full font-medium bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2"
              data-testid="delete-selected-btn"
            >
              <Trash2 size={16} /> Delete Selected ({selected.size})
            </button>
          )}
        </div>

        {/* Type & Date Filters */}
        <div className="flex flex-wrap items-end gap-4 mt-4 bg-white border border-zinc-200 p-4">
          <div className="min-w-[160px]">
            <Label>Booking Type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="filter-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="class">Class</SelectItem>
                <SelectItem value="studio">Studio</SelectItem>
                <SelectItem value="event">Event</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="filter-date-from">Class/Studio date from</Label>
            <Input id="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          </div>
          <div>
            <Label htmlFor="filter-date-to">Class/Studio date to</Label>
            <Input id="filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
          </div>
          <div>
            <Label htmlFor="filter-booked-from">Booked from</Label>
            <Input id="filter-booked-from" type="datetime-local" value={bookedFrom} onChange={(e) => setBookedFrom(e.target.value)} data-testid="filter-booked-from" />
          </div>
          <div>
            <Label htmlFor="filter-booked-to">Booked to</Label>
            <Input id="filter-booked-to" type="datetime-local" value={bookedTo} onChange={(e) => setBookedTo(e.target.value)} data-testid="filter-booked-to" />
          </div>
          <p className="text-sm text-zinc-500" data-testid="filter-result-count">Showing {bookings.length} of {meta.total}</p>
          {(typeFilter !== 'all' || dateFrom || dateTo || bookedFrom || bookedTo) && (
            <button
              onClick={() => { setTypeFilter('all'); setDateFrom(''); setDateTo(''); setBookedFrom(''); setBookedTo(''); }}
              className="text-sm underline text-zinc-600"
              data-testid="filter-clear"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Total Payout Summary (filtered, confirmed only) — owner only */}
        {isOwner && (
        <div className="mt-4 flex flex-wrap items-center gap-8 bg-black text-white p-4 rounded" data-testid="bookings-payout-summary">
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-400">Expected Payout (Confirmed, filtered)</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }} data-testid="bookings-total-payout">{peso(meta.payout)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-400">Confirmed Bookings</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }} data-testid="bookings-confirmed-count">{meta.confirmed_count}</p>
          </div>
          <div className="flex-1 min-w-[240px]">
            <p className="text-xs uppercase tracking-widest text-zinc-400 mb-2">By Payment Method</p>
            <div className="flex flex-wrap gap-2" data-testid="bookings-payout-by-method">
              {['cash', 'gcash', 'online'].map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-sm"
                  data-testid={`payout-method-${m}`}
                >
                  <span className="text-zinc-300">{methodLabels[m]}</span>
                  <span className="font-bold">{peso(payoutByMethod[m] || 0)}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Bookings Table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-100 border-b border-zinc-200">
            <tr>
              <th className="px-4 py-4 text-left">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 cursor-pointer accent-black"
                  data-testid="select-all-bookings"
                />
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold">Booking ID</th>
              <th className="px-6 py-4 text-left text-sm font-bold">Customer</th>
              <th className="px-6 py-4 text-left text-sm font-bold">Type</th>
              <th className="px-6 py-4 text-left text-sm font-bold">Date</th>
              <th className="px-6 py-4 text-left text-sm font-bold">Booked On</th>
              <th className="px-6 py-4 text-left text-sm font-bold">Status</th>
              <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.map((booking) => (
              <tr key={booking.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`booking-row-${booking.id}`}>
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selected.has(booking.id)}
                    onChange={() => toggleSelect(booking.id)}
                    className="w-4 h-4 cursor-pointer accent-black"
                    data-testid={`select-booking-${booking.id}`}
                  />
                </td>
                <td className="px-6 py-4 text-sm font-mono">{booking.id.substring(0, 8)}...</td>
                <td className="px-6 py-4">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {booking.customer_name}
                      {booking.source === 'walk-in' && (
                        <Badge variant="outline" className="border-blue-400 text-blue-600 text-[10px]" data-testid={`walkin-badge-${booking.id}`}>Walk-in</Badge>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500">{booking.customer_email || '—'}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Badge variant="outline" className="capitalize">{booking.booking_type}</Badge>
                  {booking.item_name && (
                    <p className="text-sm text-zinc-600 mt-1" data-testid={`booking-item-${booking.id}`}>{booking.item_name}</p>
                  )}
                  {booking.booking_type === 'class' && booking.instructor && (
                    <p className="text-xs text-zinc-400" data-testid={`booking-instructor-${booking.id}`}>{booking.instructor}</p>
                  )}
                </td>
                <td className="px-6 py-4 text-sm">{booking.booking_date}</td>
                <td className="px-6 py-4 text-sm text-zinc-600" data-testid={`booking-created-${booking.id}`}>{fmtDateTime(booking.created_at)}</td>
                <td className="px-6 py-4">
                  {booking.payment_status === 'confirmed' ? (
                    <Badge className="bg-green-500 hover:bg-green-600">Confirmed</Badge>
                  ) : booking.payment_status === 'rejected' ? (
                    <Badge className="bg-red-500 hover:bg-red-600" data-testid={`status-rejected-${booking.id}`}>Rejected</Badge>
                  ) : (
                    <Badge className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)]">Pending</Badge>
                  )}
                  {booking.has_payment_proof && booking.payment_status !== 'confirmed' && (
                    <Badge variant="outline" className="ml-2 border-zinc-400 text-zinc-600" data-testid={`proof-badge-${booking.id}`}>
                      Screenshot ✓
                    </Badge>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedBooking(booking)}
                      className="p-2 hover:bg-zinc-100 rounded transition-colors"
                      data-testid={`view-booking-${booking.id}`}
                    >
                      <Eye size={18} />
                    </button>
                    {booking.payment_status === 'pending' && (
                      <button
                        onClick={() => handleVerifyPayment(booking.id)}
                        className="p-2 hover:bg-green-100 text-green-600 rounded transition-colors"
                        data-testid={`verify-payment-${booking.id}`}
                      >
                        <CheckCircle size={18} />
                      </button>
                    )}
                    {booking.payment_status === 'confirmed' && (
                      <button
                        onClick={() => handleToggleAttendance(booking)}
                        className={`p-2 rounded transition-colors ${booking.attended ? 'text-green-600 hover:bg-green-100' : 'text-zinc-400 hover:bg-zinc-100'}`}
                        title={booking.attended ? 'Marked present (click to mark absent)' : 'Mark present'}
                        data-testid={`attendance-toggle-${booking.id}`}
                      >
                        {booking.attended ? <UserCheck size={18} /> : <UserX size={18} />}
                      </button>
                    )}
                    {booking.payment_status === 'confirmed' && (
                      <button
                        onClick={() => handleRevertToPending(booking.id)}
                        className="p-2 hover:bg-amber-100 text-amber-600 rounded transition-colors"
                        title="Revert to pending (undo confirmation)"
                        data-testid={`revert-booking-${booking.id}`}
                      >
                        <RotateCcw size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteBooking(booking.id)}
                      className="p-2 hover:bg-red-100 text-red-600 rounded transition-colors"
                      title="Delete booking"
                      data-testid={`delete-booking-${booking.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 && (
          <p className="p-8 text-center text-zinc-500" data-testid="bookings-empty">No bookings match these filters.</p>
        )}
      </div>

      {hasMore && (
        <div className="flex flex-col items-center gap-2 mt-6">
          <button
            onClick={() => fetchBookings('more')}
            disabled={loadingMore}
            className="bg-black text-white rounded-full px-8 py-3 font-bold transition-transform hover:scale-105 active:scale-95 disabled:opacity-60 disabled:hover:scale-100"
            data-testid="bookings-load-more-btn"
          >
            {loadingMore ? 'LOADING…' : `LOAD MORE (${meta.total - bookings.length} left)`}
          </button>
        </div>
      )}

      {/* Booking Details Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-md" data-testid="booking-details-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              Booking Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-zinc-500">Booking ID</p>
                <p className="font-mono text-sm">{selectedBooking.id}</p>
              </div>
              
              <div>
                <p className="text-sm text-zinc-500">Customer Name</p>
                <p className="font-medium">{selectedBooking.customer_name}</p>
              </div>
              
              <div>
                <p className="text-sm text-zinc-500">Email</p>
                <p className="font-medium">{selectedBooking.customer_email}</p>
              </div>
              
              <div>
                <p className="text-sm text-zinc-500">Phone</p>
                <p className="font-medium">{selectedBooking.customer_phone}</p>
              </div>
              
              <div>
                <p className="text-sm text-zinc-500">Booking Type</p>
                <p className="font-medium capitalize">{selectedBooking.booking_type}</p>
              </div>

              <div>
                <p className="text-sm text-zinc-500">{selectedBooking.booking_type === 'studio' ? 'Studio' : (selectedBooking.booking_type === 'event' ? 'Event' : 'Class')}</p>
                <p className="font-bold" data-testid="booking-item-name">{selectedBooking.item_name || '—'}</p>
                {selectedBooking.booking_type === 'class' && selectedBooking.instructor && (
                  <p className="text-sm text-zinc-600" data-testid="booking-instructor">Choreographer: <span className="font-medium">{selectedBooking.instructor}</span></p>
                )}
              </div>

              {selectedBooking.selected_addons && selectedBooking.selected_addons.length > 0 && (
                <div data-testid="booking-addons-detail">
                  <p className="text-sm text-zinc-500">Add-ons</p>
                  <ul className="mt-1 space-y-1">
                    {selectedBooking.selected_addons.map((a, i) => (
                      <li key={i} className="flex justify-between text-sm">
                        <span className="text-zinc-700">{a.name}</span>
                        <span className="font-medium">{a.price ? `₱${Number(a.price).toLocaleString('en-PH')}` : 'Free'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedBooking.accepted_terms && selectedBooking.accepted_terms.length > 0 && (
                <div data-testid="booking-terms-detail">
                  <p className="text-sm text-zinc-500">Terms Acknowledged</p>
                  <ul className="mt-1 space-y-1">
                    {selectedBooking.accepted_terms.map((t, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className={t.accepted ? 'text-green-600' : 'text-zinc-400'}>{t.accepted ? '✓' : '✗'}</span>
                        <span className="text-zinc-700">{t.text}{t.required ? '' : ' (optional)'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div>
                <p className="text-sm text-zinc-500">Date</p>
                <p className="font-medium">{selectedBooking.booking_date}</p>
              </div>

              <div>
                <p className="text-sm text-zinc-500">Booked On</p>
                <p className="font-medium" data-testid="booking-created-detail">{fmtDateTime(selectedBooking.created_at)}</p>
              </div>
              
              {selectedBooking.booking_time && (
                <div>
                  <p className="text-sm text-zinc-500">Time</p>
                  <p className="font-medium">{selectedBooking.booking_time}</p>
                </div>
              )}
              
              {selectedBooking.hours && (
                <div>
                  <p className="text-sm text-zinc-500">Hours</p>
                  <p className="font-medium">{selectedBooking.hours}</p>
                </div>
              )}
              
              {selectedBooking.booking_type === 'studio' && selectedBooking.pax && (
                <div>
                  <p className="text-sm text-zinc-500">Headcount</p>
                  <p className="font-medium" data-testid="booking-detail-headcount">{selectedBooking.pax} pax</p>
                </div>
              )}

              {selectedBooking.booking_type === 'studio' && (
                <div>
                  <p className="text-sm text-zinc-500">Purpose of Rental</p>
                  <p className="font-medium whitespace-pre-wrap" data-testid="booking-detail-purpose">{selectedBooking.rental_purpose || '—'}</p>
                </div>
              )}

              {selectedBooking.notes && (
                <div>
                  <p className="text-sm text-zinc-500">Notes</p>
                  <p className="font-medium">{selectedBooking.notes}</p>
                </div>
              )}
              
              <div>
                <p className="text-sm text-zinc-500">Payment Status</p>
                <Badge className={selectedBooking.payment_status === 'confirmed' ? 'bg-green-500' : 'bg-[var(--accent)] text-black'}>
                  {selectedBooking.payment_status}
                </Badge>
                {selectedBooking.source === 'walk-in' && (
                  <Badge variant="outline" className="ml-2 border-blue-400 text-blue-600">Walk-in</Badge>
                )}
              </div>

              {selectedBooking.payment_method && (
                <div>
                  <p className="text-sm text-zinc-500">Payment Method</p>
                  <p className="font-medium capitalize" data-testid="booking-payment-method">{selectedBooking.payment_method === 'gcash' ? 'GCash' : selectedBooking.payment_method}</p>
                </div>
              )}

              {selectedBooking.payment_status === 'confirmed' && (
                <div>
                  <p className="text-sm text-zinc-500 mb-1">Attendance</p>
                  <button
                    onClick={() => handleToggleAttendance(selectedBooking)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-colors ${selectedBooking.attended ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-600'}`}
                    data-testid="attendance-toggle-dialog"
                  >
                    {selectedBooking.attended ? <UserCheck size={18} /> : <UserX size={18} />}
                    {selectedBooking.attended ? 'Present' : 'Not marked'}
                  </button>
                </div>
              )}

              {selectedBooking.payment_status === 'confirmed' && (
                <button
                  onClick={() => handleRevertToPending(selectedBooking.id)}
                  className="w-full border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white rounded-full px-6 py-3 font-bold transition-colors flex items-center justify-center gap-2"
                  data-testid="revert-booking-dialog-btn"
                >
                  <RotateCcw size={20} />
                  REVERT TO PENDING (UNDO CONFIRMATION)
                </button>
              )}

              <div>
                <p className="text-sm text-zinc-500 mb-2">Payment Screenshot</p>
                {selectedBooking.has_payment_proof ? (
                  proofImage ? (
                    <a href={proofImage} target="_blank" rel="noopener noreferrer" data-testid="proof-image-link">
                      <img
                        src={proofImage}
                        alt="Payment proof"
                        className="max-h-72 w-full object-contain border-2 border-[var(--accent)] bg-zinc-50"
                        data-testid="proof-image"
                      />
                      <span className="text-xs text-zinc-500 mt-1 block">Click to open full size</span>
                    </a>
                  ) : (
                    <p className="text-sm text-zinc-400" data-testid="proof-loading">Loading screenshot...</p>
                  )
                ) : (
                  <p className="text-sm text-zinc-400" data-testid="no-proof">No screenshot uploaded yet</p>
                )}
              </div>

              {selectedBooking.payment_status === 'pending' && (
                <div className="space-y-3">
                  <button
                    onClick={() => handleVerifyPayment(selectedBooking.id)}
                    className="w-full bg-green-500 text-white hover:bg-green-600 rounded-full px-6 py-3 font-bold transition-colors flex items-center justify-center gap-2"
                    data-testid="verify-payment-dialog-btn"
                  >
                    <CheckCircle size={20} />
                    VERIFY PAYMENT & SEND EMAIL
                  </button>
                  {selectedBooking.has_payment_proof && (
                    <button
                      onClick={() => handleRejectPayment(selectedBooking.id)}
                      className="w-full border-2 border-red-500 text-red-600 hover:bg-red-500 hover:text-white rounded-full px-6 py-3 font-bold transition-colors flex items-center justify-center gap-2"
                      data-testid="reject-payment-dialog-btn"
                    >
                      <XCircle size={20} />
                      REJECT & REQUEST NEW SCREENSHOT
                    </button>
                  )}
                </div>
              )}

              {selectedBooking.payment_status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 p-4 text-sm text-red-700" data-testid="rejected-note">
                  Screenshot rejected. Waiting for the customer to re-upload a new payment confirmation.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Walk-In Booking Dialog */}
      <Dialog open={walkInOpen} onOpenChange={setWalkInOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="walk-in-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              Add Walk-In Booking
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleWalkInSubmit} className="space-y-4">
            <div>
              <Label htmlFor="wi-name">Customer Name *</Label>
              <Input id="wi-name" value={walkIn.customer_name} onChange={(e) => setWalkIn({ ...walkIn, customer_name: e.target.value })} required data-testid="walkin-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="wi-email">Email (optional)</Label>
                <Input id="wi-email" type="email" value={walkIn.customer_email} onChange={(e) => setWalkIn({ ...walkIn, customer_email: e.target.value })} data-testid="walkin-email-input" />
              </div>
              <div>
                <Label htmlFor="wi-phone">Phone</Label>
                <Input id="wi-phone" value={walkIn.customer_phone} onChange={(e) => setWalkIn({ ...walkIn, customer_phone: e.target.value })} data-testid="walkin-phone-input" />
              </div>
            </div>
            <div>
              <Label>Booking Type</Label>
              <Select value={walkIn.booking_type} onValueChange={(v) => setWalkIn({ ...walkIn, booking_type: v })}>
                <SelectTrigger data-testid="walkin-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="class">Class</SelectItem>
                  <SelectItem value="studio">Studio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {walkIn.booking_type === 'class' ? (
              <div>
                <Label>Class</Label>
                <Select value={walkIn.class_id} onValueChange={(v) => setWalkIn({ ...walkIn, class_id: v })}>
                  <SelectTrigger data-testid="walkin-class-select"><SelectValue placeholder="Select a class" /></SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name} • {c.class_date || c.day_of_week} {c.time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Studio</Label>
                <Select value={walkIn.studio_id} onValueChange={(v) => setWalkIn({ ...walkIn, studio_id: v })}>
                  <SelectTrigger data-testid="walkin-studio-select"><SelectValue placeholder="Select a studio" /></SelectTrigger>
                  <SelectContent>
                    {studios.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="wi-date">Date *</Label>
                <Input id="wi-date" type="date" value={walkIn.booking_date} onChange={(e) => setWalkIn({ ...walkIn, booking_date: e.target.value })} required data-testid="walkin-date-input" />
              </div>
              <div>
                <Label htmlFor="wi-time">Time</Label>
                <Input id="wi-time" type="time" value={walkIn.booking_time} onChange={(e) => setWalkIn({ ...walkIn, booking_time: e.target.value })} data-testid="walkin-time-input" />
              </div>
            </div>
            {walkIn.booking_type === 'studio' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="wi-hours">Hours</Label>
                  <Input id="wi-hours" type="number" min="1" value={walkIn.hours} onChange={(e) => setWalkIn({ ...walkIn, hours: e.target.value })} data-testid="walkin-hours-input" />
                </div>
                <div>
                  <Label htmlFor="wi-pax">Pax</Label>
                  <Input id="wi-pax" type="number" min="1" value={walkIn.pax} onChange={(e) => setWalkIn({ ...walkIn, pax: e.target.value })} data-testid="walkin-pax-input" />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="wi-purpose">Purpose of Rental</Label>
                  <Input id="wi-purpose" maxLength={500} value={walkIn.rental_purpose} onChange={(e) => setWalkIn({ ...walkIn, rental_purpose: e.target.value })} placeholder="e.g. Dance practice, content shoot" data-testid="walkin-rental-purpose-input" />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="wi-notes">Notes (Optional)</Label>
              <Textarea id="wi-notes" rows={2} maxLength={1000} value={walkIn.notes} onChange={(e) => setWalkIn({ ...walkIn, notes: e.target.value })} placeholder="Remarks for this booking, e.g. paid partial, bringing own speaker" data-testid="walkin-notes-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Payment Status</Label>
                <Select value={walkIn.payment_status} onValueChange={(v) => setWalkIn({ ...walkIn, payment_status: v })}>
                  <SelectTrigger data-testid="walkin-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed (Paid)</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={walkIn.payment_method} onValueChange={(v) => setWalkIn({ ...walkIn, payment_method: v })}>
                  <SelectTrigger data-testid="walkin-payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="gcash">GCash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
                <Label>Attendance</Label>
                <Select value={walkIn.attended ? 'yes' : 'no'} onValueChange={(v) => setWalkIn({ ...walkIn, attended: v === 'yes' })}>
                  <SelectTrigger data-testid="walkin-attended-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Present</SelectItem>
                    <SelectItem value="no">Not yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            <button type="submit" disabled={savingWalkIn} className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 disabled:opacity-60" data-testid="save-walk-in-btn">
              {savingWalkIn ? 'SAVING...' : 'CREATE BOOKING'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsManagement;